import React, { useState, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, Phone, Navigation, Clock, CheckCircle, User, DollarSign, Play, Flag, ExternalLink, AlertCircle, Camera, Upload, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const ActiveRidePanel = ({ ride, driverId, onUpdate, backendUrl }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [deliveryPhoto, setDeliveryPhoto] = useState(null);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const fileInputRef = useRef(null);

  // Check if this is a delivery
  const isDelivery = ride?.service_type === 'delivery' || ride?.type === 'delivery';

  const handleUpdateStatus = async (newStatus, endpoint) => {
    // For deliveries completing, require photo upload first
    if (isDelivery && newStatus === 'completed' && !deliveryPhoto) {
      setShowPhotoUpload(true);
      toast({
        title: "Photo Required",
        description: "Please upload a proof-of-delivery photo before completing",
        variant: "destructive"
      });
      return;
    }
    
    setIsUpdating(true);
    try {
      await axios.post(`${backendUrl}/api/drivers/rides/${ride.id}/${endpoint}?driver_id=${driverId}`);
      toast({
        title: getStatusMessage(newStatus).title,
        description: getStatusMessage(newStatus).description,
      });
      onUpdate();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update ride status",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Upload delivery photo
  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({ title: "Invalid file", description: "Please upload an image file", variant: "destructive" });
      return;
    }
    
    setIsUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${backendUrl}/api/drivers/rides/${ride.id}/delivery-photo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setDeliveryPhoto(response.data.photo_url);
      setShowPhotoUpload(false);
      toast({ title: "Photo Uploaded!", description: "Proof of delivery saved" });
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Could not upload photo",
        variant: "destructive"
      });
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // Open camera for photo capture
  const openCamera = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getStatusMessage = (status) => {
    const messages = {
      'arrived_at_pickup': { title: "Arrived!", description: "Waiting for passenger" },
      'in_progress': { title: "Trip Started!", description: "Drive safely to destination" },
      'completed': { title: "Trip Complete!", description: "Earnings added to your balance" },
    };
    return messages[status] || { title: "Updated", description: "" };
  };

  const getStatusBadge = (status) => {
    const config = {
      'driver_assigned': { label: 'En Route to Pickup', bg: 'bg-purple-100', color: 'text-purple-700', step: 1 },
      'arrived_at_pickup': { label: 'At Pickup Location', bg: 'bg-green-100', color: 'text-green-700', step: 2 },
      'in_progress': { label: 'Trip In Progress', bg: 'bg-cyan-100', color: 'text-cyan-700', step: 3 },
    };
    return config[status] || { label: status, bg: 'bg-gray-100', color: 'text-gray-700', step: 0 };
  };

  // Open navigation app (Google Maps, Apple Maps, or Waze)
  const openNavigation = (lat, lng, address) => {
    // Try to detect platform and open appropriate maps app
    const destination = lat && lng ? `${lat},${lng}` : encodeURIComponent(address);
    
    // Check if iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      // Try Apple Maps first on iOS
      window.open(`maps://maps.apple.com/?daddr=${destination}`, '_blank');
    } else {
      // Google Maps for Android and others
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=driving`, '_blank');
    }
  };

  if (!ride) return null;

  const statusBadge = getStatusBadge(ride.status);

  return (
    <Card className="p-4 sm:p-6 border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50" data-testid="active-ride-panel">
      {/* Header with Status */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg">Active Trip</h3>
        <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${statusBadge.bg} ${statusBadge.color}`}>
          {statusBadge.label}
        </span>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-4 px-2">
        {[1, 2, 3].map((step) => (
          <div key={step} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step <= statusBadge.step 
                ? 'bg-amber-500 text-white' 
                : 'bg-gray-200 text-gray-500'
            }`}>
              {step === 1 ? <Navigation size={14} /> : step === 2 ? <Play size={14} /> : <Flag size={14} />}
            </div>
            {step < 3 && (
              <div className={`w-12 sm:w-20 h-1 ${step < statusBadge.step ? 'bg-amber-500' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] sm:text-xs text-gray-500 mb-4 px-1">
        <span>Navigate</span>
        <span>Start</span>
        <span>End</span>
      </div>

      {/* Passenger Info */}
      <div className="flex items-center gap-3 mb-4 p-3 bg-white rounded-lg shadow-sm">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-100 rounded-full flex items-center justify-center">
          <User className="text-slate-600" size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{ride.passenger_name || 'Passenger'}</p>
          <p className="text-xs sm:text-sm text-gray-500">#{ride.id?.slice(0, 8)}</p>
        </div>
        {/* Privacy protected - use in-app chat instead of showing phone */}
        <div className="text-xs text-gray-400 italic hidden sm:block">
          Use chat to contact
        </div>
      </div>

      {/* Locations */}
      <div className="space-y-2 mb-4">
        <div className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] sm:text-xs text-gray-500 font-medium">PICKUP</p>
            <p className="text-xs sm:text-sm truncate">{ride.pickup?.address || 'Unknown'}</p>
          </div>
        </div>
        <div className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <MapPin size={12} className="text-red-500" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] sm:text-xs text-gray-500 font-medium">DROPOFF</p>
            <p className="text-xs sm:text-sm truncate">{ride.dropoff?.address || 'Unknown'}</p>
          </div>
        </div>
      </div>

      {/* Fare */}
      <div className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm mb-4">
        <div className="flex items-center gap-2">
          <DollarSign className="text-green-600" size={18} />
          <span className="text-sm text-gray-600">Your Earnings</span>
        </div>
        <span className="text-lg sm:text-xl font-bold text-green-600">${ride.driver_earnings || ride.estimated_price || 0}</span>
      </div>

      {/* STREAMLINED ACTION BUTTONS */}
      <div className="space-y-3">
        {/* Step 1: En Route to Pickup */}
        {ride.status === 'driver_assigned' && (
          <div className="space-y-2">
            <Button 
              className="w-full h-14 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-lg font-semibold"
              onClick={() => openNavigation(ride.pickup?.lat, ride.pickup?.lng, ride.pickup?.address)}
              data-testid="navigate-pickup-btn"
            >
              <Navigation className="mr-2" size={22} />
              Navigate to Pickup
              <ExternalLink size={14} className="ml-2 opacity-70" />
            </Button>
            <Button 
              className="w-full h-12 bg-green-600 hover:bg-green-700"
              onClick={() => handleUpdateStatus('arrived_at_pickup', 'arrived')}
              disabled={isUpdating}
              data-testid="arrived-btn"
            >
              <CheckCircle className="mr-2" size={18} />
              I've Arrived at Pickup
            </Button>
          </div>
        )}

        {/* Step 2: At Pickup - Start Trip */}
        {ride.status === 'arrived_at_pickup' && (
          <div className="space-y-2">
            <div className="p-3 bg-green-100 rounded-lg text-center mb-2">
              <p className="text-green-800 font-medium text-sm">
                ✓ You're at the pickup location
              </p>
              <p className="text-green-700 text-xs">Tap below when passenger is in vehicle</p>
            </div>
            <Button 
              className="w-full h-16 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-xl font-bold"
              onClick={() => handleUpdateStatus('in_progress', 'start')}
              disabled={isUpdating}
              data-testid="start-trip-btn"
            >
              <Play className="mr-2" size={26} />
              START TRIP
            </Button>
          </div>
        )}

        {/* Step 3: In Progress - Navigate & End Trip */}
        {ride.status === 'in_progress' && (
          <div className="space-y-2">
            <div className="p-3 bg-cyan-100 rounded-lg text-center mb-2">
              <p className="text-cyan-800 font-medium text-sm flex items-center justify-center gap-2">
                <Clock size={16} className="animate-pulse" />
                {isDelivery ? 'Delivery in progress...' : 'Trip in progress...'}
              </p>
            </div>
            
            <Button 
              className="w-full h-14 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-lg font-semibold"
              onClick={() => openNavigation(ride.dropoff?.lat, ride.dropoff?.lng, ride.dropoff?.address)}
              data-testid="navigate-dropoff-btn"
            >
              <Navigation className="mr-2" size={22} />
              Navigate to Dropoff
              <ExternalLink size={14} className="ml-2 opacity-70" />
            </Button>
            
            {/* Delivery Photo Upload Section */}
            {isDelivery && (
              <div className="my-3">
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                
                {!deliveryPhoto ? (
                  <div className={`border-2 border-dashed rounded-xl p-4 text-center ${showPhotoUpload ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}>
                    <Camera size={32} className="mx-auto mb-2 text-gray-400" />
                    <p className="text-sm font-medium text-gray-700 mb-2">Proof of Delivery Photo</p>
                    <p className="text-xs text-gray-500 mb-3">Take a photo to confirm delivery</p>
                    <Button
                      onClick={openCamera}
                      disabled={isUploadingPhoto}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                    >
                      {isUploadingPhoto ? (
                        <><Loader2 className="mr-2 animate-spin" size={18} /> Uploading...</>
                      ) : (
                        <><Camera className="mr-2" size={18} /> Take Photo</>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="border-2 border-green-400 rounded-xl p-3 bg-green-50">
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-200">
                        <img 
                          src={deliveryPhoto.startsWith('/api') ? `${backendUrl}${deliveryPhoto}` : deliveryPhoto}
                          alt="Delivery proof"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-700 flex items-center gap-1">
                          <CheckCircle size={16} /> Photo Uploaded
                        </p>
                        <p className="text-xs text-green-600">Proof of delivery saved</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={openCamera}
                        className="text-xs"
                      >
                        Retake
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <Button 
              className="w-full h-16 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-xl font-bold"
              onClick={() => handleUpdateStatus('completed', 'complete')}
              disabled={isUpdating || (isDelivery && !deliveryPhoto)}
              data-testid="end-trip-btn"
            >
              <Flag className="mr-2" size={26} />
              {isDelivery ? 'COMPLETE DELIVERY' : 'END TRIP'}
            </Button>
            
            <p className="text-center text-xs text-gray-500 mt-2">
              {isDelivery 
                ? (deliveryPhoto ? 'Tap to complete the delivery' : 'Upload proof photo to complete')
                : 'Tap "End Trip" when you arrive at the destination'
              }
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ActiveRidePanel;
